import * as stylex from '@stylexjs/stylex'

type Option<T extends string> = { label: string; value: T }

type SegmentedControlProps<T extends string> = {
    columns?: number
    onChange: (value: T) => void
    options: readonly Option<T>[]
    value: T
}

const styles = stylex.create({
    root: {
        display: 'grid',
        width: '100%',
        minWidth: 0,
        gap: '4px',
        borderRadius: '0.75rem',
        backgroundColor: '#edf3eb',
        padding: '4px',
    },
    option: {
        minWidth: 0,
        minHeight: '44px',
        borderRadius: '0.5rem',
        paddingInline: '8px',
        color: '#68756c',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '1rem',
        textTransform: 'capitalize',
        WebkitTapHighlightColor: 'transparent',
        ':hover': {
            backgroundColor: 'rgb(255 255 255 / 55%)',
        },
        ':active': {
            transform: 'scale(.98)',
        },
        ':focus-visible': {
            outline: '2px solid #4b8d5c',
            outlineOffset: '1px',
        },
    },
    active: {
        backgroundColor: '#fff',
        color: '#397a4b',
        boxShadow: '0 1px 2px rgb(38 59 42 / 8%)',
    },
})

export default function SegmentedControl<T extends string>({ columns, onChange, options, value }: SegmentedControlProps<T>) {
    return (
        <div {...stylex.props(styles.root)} role="tablist" style={{ gridTemplateColumns: `repeat(${columns ?? options.length}, minmax(0, 1fr))` }}>
            {options.map((option) => (
                <button
                    aria-selected={value === option.value}
                    {...stylex.props(styles.option, value === option.value && styles.active)}
                    key={option.value}
                    onClick={() => onChange(option.value)}
                    role="tab"
                    type="button"
                >
                    {option.label}
                </button>
            ))}
        </div>
    )
}
